import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Upload, Image, Video, Check } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';

const MediaSelectorModal = ({ isOpen, onClose, onSelect, mediaType }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  const fetchFiles = useCallback(() => {
    if (!isOpen) return;
    setLoading(true);
    fetch('/api/media')
      .then(res => res.json())
      .then(res => {
        if (res.status === 'success' && Array.isArray(res.data)) {
          setFiles(res.data);
        } else {
          setFiles([]);
        }
      })
      .catch(err => {
        console.error('Failed to fetch media:', err);
        setFiles([]);
      })
      .finally(() => setLoading(false));
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchFiles();
    }
  }, [isOpen, fetchFiles]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const isImage = (fileType) => fileType && fileType.startsWith('image/');
  const isVideo = (fileType) => fileType && fileType.startsWith('video/');

  const filteredFiles = files.filter(file => {
    if (mediaType === 'image') return isImage(file.file_type);
    if (mediaType === 'video') return isVideo(file.file_type);
    return true;
  });

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity', mediaType === 'video' ? 'video' : 'wysiwyg');

    try {
      const res = await fetch('/api/media/upload', {
        method: 'POST',
        body: formData,
      });
      const result = await res.json();
      if (result.status === 'success') {
        // Re-fetch to get the complete list with the new file
        fetchFiles();
      }
    } catch (err) {
      console.error('Upload from modal failed:', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSelect = (file) => {
    onSelect({
      file_path: file.file_path,
      file_type: file.file_type,
    });
  };

  const truncateFileName = (name, maxLen = 18) => {
    if (name.length <= maxLen) return name;
    const ext = name.split('.').pop();
    const base = name.slice(0, maxLen - ext.length - 3);
    return base + '...' + ext;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        ref={modalRef}
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-[800px] max-w-[95vw] max-h-[85vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
            {mediaType === 'video' ? t('media.insert_video') : t('media.insert_image')}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full text-slate-400 dark:text-slate-500 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Upload bar */}
        <div className="px-6 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer text-sm font-medium w-fit shadow-sm">
            <Upload size={16} />
            {uploading ? t('media.uploading') : t('media.upload_file')}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={mediaType === 'video' ? 'video/*' : 'image/*'}
              onChange={handleUpload}
              disabled={uploading}
            />
          </label>
          {uploading && (
            <span className="ml-3 text-sm text-blue-600 dark:text-blue-400 animate-pulse">
              {t('media.uploading')}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400 dark:text-slate-500">
              {mediaType === 'video' ? (
                <>
                  <Video size={48} className="mb-3 text-slate-300 dark:text-slate-600" />
                  <p>{t('media.no_videos')}</p>
                  <p className="text-sm mt-1">{t('media.please_upload')}</p>
                </>
              ) : (
                <>
                  <Image size={48} className="mb-3 text-slate-300 dark:text-slate-600" />
                  <p>{t('media.no_media_files')}</p>
                  <p className="text-sm mt-1">{t('media.please_upload')}</p>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {filteredFiles.map(file => (
                <div
                  key={file.media_id}
                  className="bg-slate-50 dark:bg-slate-700/50 rounded-xl border border-slate-200 dark:border-slate-600 overflow-hidden group hover:shadow-md hover:border-blue-300 dark:hover:border-blue-500 transition-all relative cursor-pointer"
                  onClick={() => handleSelect(file)}
                >
                  {/* Preview */}
                  <div className="aspect-square bg-slate-100 dark:bg-slate-800 flex items-center justify-center overflow-hidden">
                    {isImage(file.file_type) ? (
                      <img
                        src={file.file_path}
                        alt={file.file_path?.split('/').pop() || ''}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : isVideo(file.file_type) ? (
                      <div className="relative w-full h-full">
                        <video
                          src={file.file_path}
                          className="w-full h-full object-cover"
                          preload="metadata"
                          muted
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-10 h-10 bg-black/50 rounded-full flex items-center justify-center">
                            <Video size={18} className="text-white" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center w-full h-full">
                        <Image size={32} className="text-slate-300 dark:text-slate-600" />
                      </div>
                    )}
                  </div>

                  {/* Info & Select button */}
                  <div className="p-2 space-y-1">
                    <div className="text-xs font-medium text-slate-600 dark:text-slate-300 truncate" title={file.file_path?.split('/').pop() || ''}>
                      {truncateFileName(file.file_path?.split('/').pop() || '')}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(file);
                      }}
                      className="w-full py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100"
                    >
                      <Check size={12} />
                      {t('media.select')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaSelectorModal;
